import { useState, useEffect } from 'react';
import { Events } from 'react-scroll';

/**
 * Configuration options for the ScrollSpy hook
 * @typedef {object} ScrollSpyOptions
 * @property {number} [offset=0] - Scroll offset for sticky headers
 * @property {number} [duration=500] - Scroll animation duration in ms
 * @property {string} [containerId='window'] - Scroll container ID or 'window'
 * @property {boolean} [smooth=true] - Enable smooth scrolling
 * @property {number} [delay=0] - Delay before scroll starts
 */

/**
 * React-scroll integration hook for scroll spy functionality
 * Tracks the currently visible section and provides scroll navigation
 * @param {string[]} sectionIds - Array of section IDs to observe
 * @returns {{
 *   activeSection: string | null,
 *   setActiveSection: (sectionId: string) => void,
 *   isScrolling: boolean
 * }} ScrollSpy state and methods
 */
function useScrollSpy(sectionIds = []) {
  // State management
  const [activeSection, setActiveSection] = useState(null);
  const [isScrolling, setIsScrolling] = useState(false);

  /**
   * Set up react-scroll event listeners for scroll spy functionality
   */
  useEffect(() => {
    if (!sectionIds.length) return undefined;

    /**
     * Handle scroll animation start
     */
    const handleScrollBegin = () => {
      setIsScrolling(true);
    };

    /**
     * Handle scroll animation end
     * @param {string} to - Target section ID
     */
    const handleScrollEnd = (to) => {
      setActiveSection(to);
    };

    // Register scroll events
    Events.scrollEvent.register('begin', handleScrollBegin);
    Events.scrollEvent.register('end', handleScrollEnd);

    // Clean up event listeners on unmount or dependency change

    return () => {
      Events.scrollEvent.remove('begin');
      Events.scrollEvent.remove('end');
    };
  }, [sectionIds]);

  /**
   * Initialize active section from URL hash or first section
   */
  useEffect(() => {
    if (!sectionIds.length) return;

    const hash = window.location.hash.slice(1);
    if (hash && sectionIds.includes(hash)) {
      setActiveSection(hash);
    } else {
      // Set first section as active by default
      setActiveSection(sectionIds[0]);
    }
  }, [sectionIds]);

  return {
    activeSection,
    setActiveSection,
    isScrolling,
  };
}

export default useScrollSpy;
